import { POST_FLIGHT_DOUBLE_DOWN_TASK_TYPE, type TaskRecord } from "@nexus/contracts";

const PERSONA_SCRIPTS_GENERATION_TASK_TYPES = new Set<string>([
  "generate-persona-scripts",
  POST_FLIGHT_DOUBLE_DOWN_TASK_TYPE,
]);

export function isPersonaScriptsGenerationTask(task: Pick<TaskRecord, "type">): boolean {
  return PERSONA_SCRIPTS_GENERATION_TASK_TYPES.has(task.type);
}

function hasIncompleteProgress(task: TaskRecord): boolean {
  return task.stages.some((stage) =>
    stage.subtasks.some((subtask) => subtask.progress < 1),
  );
}

function isActiveScriptsTaskForSet(task: TaskRecord, setId: string): boolean {
  return (
    isPersonaScriptsGenerationTask(task) &&
    task.navigation?.params?.setId === setId &&
    hasIncompleteProgress(task)
  );
}

/** True while a persona-scripts task for this set still has incomplete progress. */
export function isPersonaScriptsGenerationActiveForSet(
  tasks: TaskRecord[] | undefined,
  setId: string | null | undefined,
): boolean {
  if (!tasks?.length || !setId) return false;
  return tasks.some((task) => isActiveScriptsTaskForSet(task, setId));
}

export function isPersonaScriptsGenerationActiveForAnySet(
  tasks: TaskRecord[] | undefined,
  setIds: readonly string[],
): boolean {
  if (!tasks?.length || setIds.length === 0) return false;
  return setIds.some((setId) => isPersonaScriptsGenerationActiveForSet(tasks, setId));
}

/** Correlation ids whose persona-scripts task is still in progress. */
export function inProgressPersonaScriptsGenerationIds(
  tasks: readonly TaskRecord[],
): Set<string> {
  const ids = new Set<string>();
  for (const task of tasks) {
    if (isPersonaScriptsGenerationTask(task) && hasIncompleteProgress(task)) {
      ids.add(task.correlationId);
    }
  }
  return ids;
}

/**
 * Ids that were in progress last poll and are no longer (progress 1, or the
 * task left the active list). First sight of an already-complete card is not
 * a finish — that would refetch on every page load with a leftover sidebar card.
 */
export function detectFinishedPersonaScriptsGenerationIds(
  previouslyInProgress: ReadonlySet<string>,
  tasks: readonly TaskRecord[],
): { inProgress: Set<string>; finishedIds: string[] } {
  const inProgress = inProgressPersonaScriptsGenerationIds(tasks);
  const finishedIds: string[] = [];
  for (const id of previouslyInProgress) {
    if (!inProgress.has(id)) {
      finishedIds.push(id);
    }
  }
  return { inProgress, finishedIds };
}
